import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Search, Download, Inbox } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { api, API_BASE, getToken, setToken, clearToken, errorMessage } from "@/lib/api";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LoginForm = ({ onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Enter your username and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        username: username.trim(),
        password,
      });
      setToken(data.access_token);
      setPassword("");
      onSuccess();
    } catch (err) {
      setError(errorMessage(err, "Could not sign in. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F8F9FA] px-6">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-[0_30px_80px_-40px_rgba(9,9,11,0.25)]">
          <div className="h-11 w-11 rounded-full bg-[#0047FF]/10 text-[#0047FF] grid place-items-center">
            <Lock size={20} />
          </div>
          <h1 className="font-display mt-5 text-2xl font-semibold tracking-tight text-zinc-950">
            Leads dashboard
          </h1>
          <p className="mt-2 text-sm text-zinc-600">
            Sign in to view contact form submissions.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-5" data-testid={ADMIN.loginForm}>
            <div>
              <Label htmlFor="admin-username" className="text-zinc-800">
                Username
              </Label>
              <Input
                id="admin-username"
                data-testid={ADMIN.usernameInput}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-zinc-800">
                Password
              </Label>
              <Input
                id="admin-password"
                type="password"
                data-testid={ADMIN.passwordInput}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-2 h-11"
              />
            </div>

            {error && (
              <p data-testid={ADMIN.loginError} className="text-sm text-red-600">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid={ADMIN.loginSubmit}
              disabled={loading}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800 rounded-full h-11"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-zinc-500">
          Zaplee.ai — internal use only
        </p>
      </div>
    </div>
  );
};

const LeadsTable = ({ onSignOut }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/leads");
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        onSignOut();
        return;
      }
      setError(errorMessage(err, "Could not load leads."));
    } finally {
      setLoading(false);
    }
  }, [onSignOut]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.company, l.role, l.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [leads, query]);

  const downloadCsv = async () => {
    try {
      // Fetched through axios so the Authorization header is attached; a plain
      // link would hit the endpoint unauthenticated and 401.
      const { data } = await api.get("/leads.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zaplee-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, "Could not export CSV."));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-lg font-semibold tracking-tight text-zinc-950">
              Leads
            </span>
            <span className="font-mono-label text-[10px] text-[#0047FF]">
              // {leads.length} total
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="rounded-full"
              data-testid={ADMIN.refresh}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="ml-1.5 hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCsv}
              className="rounded-full"
              data-testid={ADMIN.exportCsv}
            >
              <Download size={14} />
              <span className="ml-1.5 hidden sm:inline">CSV</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSignOut}
              className="rounded-full"
              data-testid={ADMIN.signOut}
            >
              <LogOut size={14} />
              <span className="ml-1.5 hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-8">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, company…"
            className="h-11 pl-10"
            data-testid={ADMIN.search}
          />
        </div>

        {error && (
          <p data-testid={ADMIN.error} className="mt-6 text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="mt-6 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
          {loading ? (
            <p className="p-10 text-center text-sm text-zinc-500">Loading leads…</p>
          ) : filtered.length === 0 ? (
            <div className="p-14 text-center" data-testid={ADMIN.empty}>
              <div className="mx-auto h-12 w-12 rounded-full bg-zinc-100 text-zinc-400 grid place-items-center">
                <Inbox size={22} />
              </div>
              <p className="mt-4 text-sm text-zinc-600">
                {leads.length === 0
                  ? "No leads yet. Submissions from the contact form will appear here."
                  : "No leads match that search."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={ADMIN.table}>
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/80 text-left">
                    <th className="px-5 py-3 font-medium text-zinc-600">Name</th>
                    <th className="px-5 py-3 font-medium text-zinc-600">Email</th>
                    <th className="px-5 py-3 font-medium text-zinc-600">Company</th>
                    <th className="px-5 py-3 font-medium text-zinc-600">Role</th>
                    <th className="px-5 py-3 font-medium text-zinc-600">Message</th>
                    <th className="px-5 py-3 font-medium text-zinc-600 whitespace-nowrap">
                      Received
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => (
                    <tr
                      key={lead.id}
                      className="border-b border-zinc-100 last:border-0 align-top hover:bg-zinc-50/60"
                      data-testid={ADMIN.row}
                    >
                      <td className="px-5 py-4 font-medium text-zinc-900 whitespace-nowrap">
                        {lead.name}
                      </td>
                      <td className="px-5 py-4">
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-[#0047FF] hover:underline underline-offset-4"
                        >
                          {lead.email}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-zinc-700">{lead.company}</td>
                      <td className="px-5 py-4 text-zinc-500">{lead.role || "—"}</td>
                      <td className="px-5 py-4 text-zinc-600 max-w-md">
                        <span className="line-clamp-3">{lead.message || "—"}</span>
                      </td>
                      <td className="px-5 py-4 text-zinc-500 whitespace-nowrap">
                        {formatDate(lead.created_at)}
                        {typeof lead.captcha_score === "number" && (
                          <span
                            className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-mono ${
                              lead.captcha_score < 0.5
                                ? "bg-amber-100 text-amber-700"
                                : "bg-zinc-100 text-zinc-500"
                            }`}
                            title="reCAPTCHA score (1.0 = very likely human)"
                          >
                            {lead.captcha_score.toFixed(1)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-zinc-400">API: {API_BASE}</p>
      </main>
    </div>
  );
};

const AdminLeads = () => {
  // "checking" until a stored token is validated, so a reload does not flash
  // the login form at an already-authenticated admin.
  const [state, setState] = useState(getToken() ? "checking" : "anonymous");

  useEffect(() => {
    // The admin dashboard must never be indexed.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const previousTitle = document.title;
    document.title = "Leads — Zaplee.ai";
    return () => {
      meta.remove();
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (state !== "checking") return;
    let cancelled = false;
    api
      .get("/auth/me")
      .then(() => {
        if (!cancelled) setState("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        clearToken();
        setState("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  const signOut = useCallback(() => {
    clearToken();
    setState("anonymous");
  }, []);

  if (state === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8F9FA]">
        <p className="text-sm text-zinc-500">Checking your session…</p>
      </div>
    );
  }

  if (state === "anonymous") {
    return <LoginForm onSuccess={() => setState("authenticated")} />;
  }

  return <LeadsTable onSignOut={signOut} />;
};

export default AdminLeads;
